import React, { useState, useEffect, useMemo } from 'react';

import moment from 'moment';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import Tooltip from '../../Tooltip';
import EditableTitle from '../../Library/EditableTitle';
import TitleWithProgress from '../TitleWithProgress';
import ActionsMenu from '../../Library/ActionsMenu';
import TableRowAction from '../../Library/TableRowAction';
import MeetingThumbnail from '../../Library/MeetingThumbnail';
import NotificationManagerModal from '../../../modals/NotificationManagerModal';

import downloadVideo from '../../../helpers/downloadVideo';
import { ORG_ROLES, MEETING_TABS, USER_ROLES } from '../../../constants/main';
import formatDuration from '../../../helpers/formatDuration';
import useMeetingProcessing from '../../../hooks/useMeetingProcessing';
import playIcon from '../../../assets/images/my-meetings/play.svg';
import deleteIcon from '../../../assets/images/delete.svg';
// eslint-disable-next-line import/no-named-as-default
import useAuthorization from '../../../hooks/useAuthorization';
import DraftBadge from '../../DraftBadge';

export default function Row({
  classes,
  meeting,
  isMobile,
  tab,
  user,
  handleUpdateMeeting,
  handlePlay,
  handleCopyFolderLink,
  handleMeetingClick,
  destinationMeetingId,
  handleDetails,
  handleDelete,
  setCurrentContentTitle,
  setCurrentMeetingId,
  setIsSummaryModalVisible,
  refetch,
  handleRequestReview,
  handleManageAccess,
  handleMove,
}) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [
    isNotificationManagerVisible,
    setIsNotificationManagerVisible,
  ] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(null);

  const navigate = useNavigate();
  const processing = useMeetingProcessing();

  // Set processing progress, filter by meeting id
  useEffect(() => {
    if (processing.meetingId === meeting.id) {
      setProcessingProgress(processing);
    }
  }, [meeting.id, processing]);

  const isMeetingPublished = meeting.status === 'published';
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;
  const isMeetingInProgress =
    meeting.status === 'in progress' ||
    (meeting.type === 'mobile uploads' && meeting.status === 'draft');
  const isMenuDisabled = isMeetingInProgress && !isSuperAdmin;

  const isMeetingExpired =
    meeting.expiresAt && new Date(meeting.expiresAt) < new Date();

  const isNotAllowedToDownload =
    !isMeetingPublished ||
    user?.orgRole?.access !== ORG_ROLES.OWNER;

  const isNotAllowedToView =
    !isMeetingPublished || (isMeetingExpired && meeting.userId !== user?.id);

  // Use authorization hook for permission checks
  const resource = useMemo(() => ({ userId: meeting.userId }), [meeting.userId]);
  const resourceUser = useMemo(() => 
    meeting.userRole ? { role: meeting.userRole } : null, 
    [meeting.userRole]
  );
  
  const { 
    canEditMeeting,
    canDeleteMeeting,
    canManageMeetingPermissions 
  } = useAuthorization(
    user,
    resource,
    resourceUser,
    'meeting',
    {
      folderId: meeting.folderId,
      folderUserRole: meeting.folderUserRole,
    }
  );

  const isNotAllowedToEdit =
    !canEditMeeting ||
    isMenuDisabled ||
    meeting.type === 'mobile uploads' ||
    (tab !== MEETING_TABS.MY_VIDEOS && tab !== MEETING_TABS.MOBILE_UPLOADS);

  const isNotAllowedToDelete =
    (meeting.status === 'in progress' && !isSuperAdmin) || !canDeleteMeeting;

  const isNotAllowedToShare = !canManageMeetingPermissions;

  const isNotAllowedToCopyLink = !canManageMeetingPermissions;

  const isNotAllowedToRequestReview = !canManageMeetingPermissions;

  const isNotAllowedToGetViewerReport =
    !isMeetingPublished || meeting.userRole === 'viewer';

  const duration = meeting.duration && formatDuration(meeting.duration);

  const lastPublishedDate = isMeetingPublished
    ? moment(meeting.updatedAt).format('MMM D, YYYY')
    : 'Not Published';

  const uploadedDate = moment(meeting.createdAt).format('MMM D, YYYY');

  // Check if meeting has a published version
  const hasPublishedVersion = meeting.latestPublishedVersion != null;
  const latestVersion = meeting.latestPublishedVersion;

  // Check if meeting has draft data
  const hasDraft = meeting.draftData && meeting.draftData.sectionIds && meeting.draftData.sectionIds.length > 0;

  const title = (
    <EditableTitle
      handleUpdateTitle={(newTitle) =>
        handleUpdateMeeting(meeting.id, newTitle)
      }
      isEditable={!isNotAllowedToEdit}
      isEditing={isEditingTitle}
      setIsEditing={setIsEditingTitle}
    >
      {meeting.title}
    </EditableTitle>
  );

  const playButton = (
    <button
      className={classes.playButton}
      disabled={isNotAllowedToView}
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        handlePlay(meeting.id);
      }}
    >
      <img src={playIcon} alt="Play" />
    </button>
  );

  return (
    <tr
      onClick={() => handleMeetingClick(meeting.id)}
      key={meeting.id}
      className={cn({
        [classes.clickable]: destinationMeetingId,
      })}
      style={hasDraft ? { backgroundColor: '#fff9e6' } : {}}
      data-testid={`meeting-row-${meeting.id}`}
    >
      <td className={classes.title}>
        <div className={classes.left}>
          <MeetingThumbnail meeting={meeting} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <TitleWithProgress
              classes={classes}
              title={title}
              isMeetingPublished={!isMeetingInProgress}
              lastPublishedDate={lastPublishedDate}
              processing={{
                step: processingProgress?.step || (meeting?.type === 'saved' ? 'Processing' : 'Publishing'),
                percentage: processingProgress?.percentage || 0,
              }}
              meetingId={meeting.id}
            />
          </div>
        </div>

        <div className={classes.right}>
          {playButton}
          {!isMobile && !isNotAllowedToDelete && (
            <button
              data-testid={`delete-meeting-button-${meeting.id}`}
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(meeting.id);
              }}
              type="button"
            >
              <img src={deleteIcon} alt="Delete" />
            </button>
          )}
        </div>
      </td>
      <td className={classes.owner}>
        {meeting.userId === user?.id
          ? 'Me'
          : meeting.ownerName || meeting.Owner?.name}
      </td>
      <td className={classes.status}>{uploadedDate}</td>
      <td className={classes.duration}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {isNotAllowedToView ? (
            <Tooltip
              position="left"
              text={
                canEditMeeting
                  ? "This video isn't published yet. Click the pencil icon to edit, then select Publish to make it visible."
                  : "This video isn't published yet."
              }
              width={200}
            >
              {playButton}
            </Tooltip>
          ) : (
            playButton
          )}
          {isNotAllowedToView ? (
            <span className={classes.noDuration}>{duration || 'NA'}</span>
          ) : (
            duration
          )}
        </div>
      </td>
      <td className={classes.status} style={{ width: '1%', whiteSpace: 'nowrap' }}>
        {hasPublishedVersion && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/my-videos/${meeting.id}/history`);
            }}
            style={{
              fontSize: '20px',
              color: '#007bff',
              fontWeight: 500,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            v{Number(latestVersion)}
          </button>
        )}
        {!hasPublishedVersion && hasDraft && <DraftBadge />}
      </td>
      {!isMobile && (
        <td className={classes.actions}>
          <TableRowAction
            handleDelete={() => handleDelete(meeting.id)}
            handleEdit={() => {
              navigate(`/my-videos/${meeting.id}/edit`);
            }}
            handleManageAccess={handleManageAccess ? () => handleManageAccess(meeting) : undefined}
            noManageAccess={false}
            canEdit={!isNotAllowedToEdit}
            canDelete={canDeleteMeeting}
            canManageAccess={canManageMeetingPermissions}
            meetingId={meeting.id}
            disabled={isMenuDisabled}
            disabledTooltip="This video is currently being published."
          />
        </td>
      )}
      <td
        className={cn({
          [classes.buttons]: !isMobile,
          [classes.mobileActionsMenu]: isMobile,
        })}
      >
        <div className={classes.buttonsContainer}>
          <div className={classes.actionsContainer}>
            <ActionsMenu
              data={meeting}
              handleDetails={() => handleDetails(meeting)}
              handleMoveToTrash={() => handleDelete(meeting.id)}
              handleEdit={() => {
                // navigate(`/my-videos/${meeting.id}/edit`)
                setIsEditingTitle(true);
              }}
              enableMobileDelete={tab === MEETING_TABS.MOBILE_UPLOADS}
              handleShare={() =>
                navigate(`/my-videos/${meeting.id}/edit?share=true`)
              }
              handleDownload={() => downloadVideo(meeting)}
              handleRequestReview={
                handleRequestReview
                  ? () => handleRequestReview(meeting.id)
                  : undefined
              }
              handleGetViewerReport={() => {
                setCurrentContentTitle(meeting.title);
                setCurrentMeetingId(meeting.id);
                setIsSummaryModalVisible(true);
              }}
              handleCopyLink={() =>
                handleCopyFolderLink(meeting.folderId, meeting.id)
              }
              isNotAllowedToDelete={isNotAllowedToDelete}
              isNotAllowedToEdit={isNotAllowedToEdit}
              isNotAllowedToShare={isNotAllowedToShare}
              isNotAllowedToDownload={isNotAllowedToDownload}
              isNotAllowedToCheckDetails={false}
              isNotAllowedToGetViewerReport={isNotAllowedToGetViewerReport}
              isNotAllowedToCopyLink={isNotAllowedToCopyLink}
              isNotAllowedToSetReminder={isNotAllowedToEdit}
              isNotAllowedToRequestReview={isNotAllowedToRequestReview}
              disabled={isMenuDisabled}
              disabledTooltip="This video is currently being published."
              handleSetReminder={() => setIsNotificationManagerVisible(true)}
              handleMove={handleMove ? () => handleMove(meeting) : undefined}
            />
          </div>
        </div>
      </td>
      <NotificationManagerModal
        show={isNotificationManagerVisible}
        handleClose={() => setIsNotificationManagerVisible(false)}
        reminder={meeting?.EmailReminder}
        meetingId={meeting?.id}
        refetch={refetch}
      />
    </tr>
  );
}
